import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRequestMachinesBatch } from "@/hooks/use-machines";
import { TOAST_SUCCESS_DURATION_MS } from "@/lib/toast-durations";
import type { Machine, MachineRequest } from "@/lib/types";
import { cn } from "@/lib/utils";

interface BatchRequestProgressDialogProps {
  machines: Machine[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface BatchRequestResult {
  id: number;
  ok: boolean;
  request: MachineRequest | null;
  error: string | null;
}

export function BatchRequestProgressDialog({
  machines,
  open,
  onOpenChange,
}: BatchRequestProgressDialogProps) {
  const batch = useRequestMachinesBatch();
  const [elapsedMs, setElapsedMs] = useState(0);
  const [results, setResults] = useState<BatchRequestResult[] | null>(null);
  // Snapshot on open so an in-flight refetch can't shrink the rendered list
  // when the upstream selection sheds the just-requested rows.
  const [snapshot, setSnapshot] = useState<Machine[]>([]);

  useEffect(() => {
    if (open && snapshot.length === 0 && machines.length > 0) {
      setSnapshot(machines);
    }
  }, [open, snapshot.length, machines]);

  useEffect(() => {
    if (!batch.isPending) return;
    const startedAt = performance.now();
    const interval = setInterval(() => {
      setElapsedMs(performance.now() - startedAt);
    }, 100);
    return () => clearInterval(interval);
  }, [batch.isPending]);

  useEffect(() => {
    if (!open) {
      setElapsedMs(0);
      setResults(null);
      setSnapshot([]);
      batch.reset();
    }
  }, [open, batch]);

  useEffect(() => {
    if (open && snapshot.length > 0 && results === null && !batch.isPending && !batch.isError) {
      batch.mutate(
        { machineIds: snapshot.map((machine) => machine.id) },
        {
          onSuccess: (data) => {
            setResults(data.results);
          },
        },
      );
    }
  }, [open, snapshot, results, batch]);

  const isPending = batch.isPending;
  const isMutationError = batch.isError && results === null;
  const errorMessage = batch.error instanceof Error ? batch.error.message : null;
  const succeeded = results?.filter((result) => result.ok).length ?? 0;
  const failed = results ? results.length - succeeded : 0;
  const elapsedSeconds = Math.floor(elapsedMs / 1000);
  const resultsById = new Map(results?.map((result) => [result.id, result]));

  // Auto-close on clean success.
  const autoCloseDoneRef = useRef(false);
  useEffect(() => {
    if (!results || autoCloseDoneRef.current || failed > 0) return;
    autoCloseDoneRef.current = true;
    const names = results
      .filter((result) => result.ok)
      .map(
        (result) => snapshot.find((machine) => machine.id === result.id)?.name ?? `id=${result.id}`,
      );
    toast.success(`Requested ${names.length} machine${names.length === 1 ? "" : "s"}`, {
      description: `${names.join(", ")} — owners have 30 minutes to accept or deny.`,
      duration: TOAST_SUCCESS_DURATION_MS,
    });
    onOpenChange(false);
  }, [results, failed, snapshot, onOpenChange]);

  useEffect(() => {
    if (!open) autoCloseDoneRef.current = false;
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isPending) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="border-border bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {results
              ? "Batch request complete"
              : `Requesting ${snapshot.length} machine${snapshot.length === 1 ? "" : "s"}`}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {results ? (
              <>
                <span className="text-emerald-600 dark:text-emerald-400">{succeeded} sent</span>
                {failed > 0 && (
                  <>
                    {" · "}
                    <span className="text-destructive">{failed} failed</span>
                  </>
                )}
              </>
            ) : (
              <>Sending Slack DMs in parallel — {elapsedSeconds}s elapsed.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {isMutationError && (
          <div className="flex items-start gap-2.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-foreground">Batch request failed</span>
              <span className="text-xs text-muted-foreground">
                {errorMessage ?? "Unknown error — check the network tab."}
              </span>
            </div>
          </div>
        )}

        <div className="flex max-h-[50vh] flex-col gap-1.5 overflow-y-auto pr-1">
          {snapshot.map((machine) => {
            const result = resultsById.get(machine.id);
            const isDone = result !== undefined;
            const isOk = isDone && result.ok;
            return (
              <div
                key={machine.id}
                className={cn(
                  "flex items-center gap-2.5 rounded-md border px-3 py-2 transition-colors",
                  !isDone && "border-primary/30 bg-primary/5",
                  isOk && "border-emerald-500/40 bg-emerald-500/5",
                  isDone && !isOk && "border-destructive/40 bg-destructive/5",
                )}
              >
                {isDone ? (
                  isOk ? (
                    <Check className="size-4 shrink-0 text-emerald-500 animate-in zoom-in-50" />
                  ) : (
                    <X className="size-4 shrink-0 text-destructive animate-in zoom-in-50" />
                  )
                ) : (
                  <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
                )}
                <span className="flex-1 truncate text-sm font-medium text-foreground">
                  {machine.name}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {machine.owner_name ?? ""}
                </span>
                {isDone && !isOk && (
                  <span className="ml-auto truncate text-xs text-destructive">{result.error}</span>
                )}
              </div>
            );
          })}
        </div>

        {!isPending && (
          <DialogFooter>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className={cn(
                failed > 0
                  ? "bg-amber-600 text-white hover:bg-amber-500"
                  : "bg-emerald-600 text-white hover:bg-emerald-500",
              )}
            >
              Done
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
